'use client'

import { useCallback, useMemo, useRef } from 'react'
import Turnstile, { type TurnstileHandle } from '../Turnstile'

// The real managed sitekey's domain allowlist does not cover localhost, so the
// invisible widget silently never completes in local dev (no token, no error
// code — just the tiles spinning forever). `next dev` sets NODE_ENV to
// 'development' (a `next build` for qa/staging/prod sets 'production'), so in
// local dev only we fall back to Cloudflare's official always-pass TEST sitekey
// — the real Turnstile flow, issuing a dummy token the backend validates with
// the matching test secret (see getpostimagebykey). Prod is untouched.
const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA'
export const TURNSTILE_SITEKEY = process.env.NODE_ENV === 'production'
  ? (process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? '')
  : TURNSTILE_TEST_SITEKEY

/**
 * One invisible Turnstile widget and the token getter every screenshot fetch
 * on the page goes through (/api/screenshots/bykey needs a fresh token per
 * request). Render `turnstile` once, anywhere that stays mounted while tokens
 * are needed, and hand `getToken` down.
 *
 * The widget issues one token at a time, so calls are serialised through a
 * queue: a second caller waits for the first instead of overwriting its
 * pending resolver (which left the first tile on "Loading" forever when the
 * page's widgets each called an un-queued getter). A Turnstile that errors,
 * times out or never mounts rejects, and a hard 15 s deadline covers the
 * origins where the challenge silently never completes, so a failed fetch
 * falls to the widget's 'error' state and its Retry instead of hanging.
 *
 * Stable identities throughout: <Turnstile> re-renders the widget whenever a
 * callback prop changes, so a fresh inline function each render would tear
 * it down mid-challenge ("Cannot find Widget ...").
 */
export function useScreenshotToken(): { turnstile: React.ReactElement; getToken: () => Promise<string> } {
  const widgetRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)
  const pendingRejectRef = useRef<((err: Error) => void) | null>(null)

  const handleVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
    pendingRejectRef.current = null
  }, [])
  const handleFail = useCallback((why: string) => {
    pendingRejectRef.current?.(new Error(`turnstile ${why}`))
    pendingTokenRef.current = null
    pendingRejectRef.current = null
  }, [])
  const handleError = useCallback(() => handleFail('error'), [handleFail])
  const handleTimeout = useCallback(() => handleFail('timeout'), [handleFail])

  const queueRef = useRef<Promise<unknown>>(Promise.resolve())
  const getToken = useCallback((): Promise<string> => {
    const task = () => new Promise<string>((resolve, reject) => {
      pendingTokenRef.current = resolve
      pendingRejectRef.current = reject
      const tryRun = (attempt = 0) => {
        if (widgetRef.current) {
          widgetRef.current.reset()
          widgetRef.current.run()
          return
        }
        if (attempt < 20) { setTimeout(() => tryRun(attempt + 1), 100); return }
        // widget never mounted -> don't hang the promise
        reject(new Error('turnstile widget unavailable'))
        pendingTokenRef.current = null
        pendingRejectRef.current = null
      }
      tryRun()
    })
    const withDeadline = () => new Promise<string>((resolve, reject) => {
      const t = setTimeout(() => {
        pendingTokenRef.current = null
        pendingRejectRef.current = null
        reject(new Error('turnstile token deadline (no challenge completion)'))
      }, 15000)
      task().then((tok) => { clearTimeout(t); resolve(tok) }, (e) => { clearTimeout(t); reject(e) })
    })
    const run = queueRef.current.then(withDeadline, withDeadline)
    queueRef.current = run.then(() => undefined, () => undefined)
    return run
  }, [])

  const turnstile = useMemo(
    () => <Turnstile ref={widgetRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleVerify} onError={handleError} onTimeout={handleTimeout} />,
    [handleVerify, handleError, handleTimeout],
  )

  return { turnstile, getToken }
}
